package com.trevorwiebe.timesheet.admin.customer.presentation

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.admin.customer.domain.CustomerRepository
import com.trevorwiebe.timesheet.core.domain.model.Customer
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class CustomersViewModel(
    private val customerRepository: CustomerRepository
) : DisposableViewModel() {

    private val _state = MutableStateFlow(CustomersState())
    val state = _state.asStateFlow()

    init {
        loadCustomers()
    }

    fun onEvent(event: CustomersEvent) {
        when (event) {
            is CustomersEvent.OnAddCustomerClick -> {
                _state.update { it.copy(showAddEditDialog = true, editingCustomer = null) }
            }
            is CustomersEvent.OnEditCustomerClick -> {
                _state.update { it.copy(showAddEditDialog = true, editingCustomer = event.customer) }
            }
            is CustomersEvent.OnDismissDialog -> {
                _state.update { it.copy(showAddEditDialog = false, editingCustomer = null) }
            }
            is CustomersEvent.OnSaveCustomer -> {
                saveCustomer(event)
            }
            is CustomersEvent.OnDeleteCustomer -> {
                deleteCustomer()
            }
            is CustomersEvent.OnRestoreCustomer -> {
                restoreCustomer()
            }
            is CustomersEvent.OnFilterToggled -> {
                _state.update { state ->
                    val current = state.activeFilters
                    val updated = if (event.filter in current) current - event.filter else current + event.filter
                    state.copy(activeFilters = updated)
                }
            }
            is CustomersEvent.OnErrorShown -> {
                _state.update { it.copy(error = null) }
            }
        }
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadCustomers() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true) }
            val flow = customerRepository.getCustomers()
            flow.collect { result ->
                if (result.error != null) {
                    _state.update { it.copy(isLoading = false, error = result.error) }
                } else {
                    val customers = result.data as List<Customer>
                    _state.update { it.copy(isLoading = false, customers = customers, error = null) }
                }
            }
        }
    }

    private fun saveCustomer(event: CustomersEvent.OnSaveCustomer) {
        viewModelScope.launch {
            val editing = _state.value.editingCustomer
            val result = if (editing == null) {
                customerRepository.addCustomer(
                    Customer(
                        id = "",
                        contactName = event.contactName.trim(),
                        companyName = event.companyName.trim(),
                        address = event.address.trim(),
                        phone = event.phone.trim(),
                        emails = event.emails.map { it.trim() }.filter { it.isNotEmpty() },
                        paymentTerms = event.paymentTerms.trim(),
                        notes = event.notes.trim(),
                        deleted = false
                    )
                )
            } else {
                customerRepository.updateCustomer(
                    editing.copy(
                        contactName = event.contactName.trim(),
                        companyName = event.companyName.trim(),
                        address = event.address.trim(),
                        phone = event.phone.trim(),
                        emails = event.emails.map { it.trim() }.filter { it.isNotEmpty() },
                        paymentTerms = event.paymentTerms.trim(),
                        notes = event.notes.trim()
                    )
                )
            }
            _state.update {
                it.copy(showAddEditDialog = false, editingCustomer = null, error = result.error)
            }
        }
    }

    private fun deleteCustomer() {
        viewModelScope.launch {
            val editing = _state.value.editingCustomer ?: return@launch
            val result = customerRepository.updateCustomer(editing.copy(deleted = true))
            _state.update {
                it.copy(showAddEditDialog = false, editingCustomer = null, error = result.error)
            }
        }
    }

    private fun restoreCustomer() {
        viewModelScope.launch {
            val editing = _state.value.editingCustomer ?: return@launch
            val result = customerRepository.updateCustomer(editing.copy(deleted = false))
            _state.update {
                it.copy(showAddEditDialog = false, editingCustomer = null, error = result.error)
            }
        }
    }
}
